// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
// eslint-disable-next-line
import Vue from 'vue';
import moment from 'moment';
import App from './App';
import router from './router';
import axios from 'axios';
import { ACCESS_TYPE } from '@/constants';
import { getApiBaseUrl } from '@/utils/apiBase';

function whenDomReady (fn) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', fn);
    } else {
        fn();
    }
}

function setupChatVuePlugins (lkeComponent) {
    if (typeof Vue === 'undefined') {
        console.error('Vue is not available');
        return;
    }
    Vue.use(lkeComponent);
    const vhtml = window.vhtml;
    if (vhtml && vhtml.install) {
        Vue.use(vhtml);
        vhtml.install(Vue);
        try {
            const iconpath = 'https://cdn3.codesign.qq.com/icons/XzKaDZdEa7j2GPL/latest/iconfont.js';
            vhtml.Icon.setDefaultRemoteSrc && vhtml.Icon.setDefaultRemoteSrc(iconpath);
        } catch (e) {
            console.log('load remote icon error');
        }
    } else {
        console.warn('vhtml plugin not found');
    }
}

async function fetchLkeToken (guestId) {
    const res = await axios.get(`${getApiBaseUrl()}/getDemoToken`, {
        params: { visitorBizId: guestId },
        timeout: 15000
    });
    const apiResponse = res && res.data && res.data.apiResponse;
    const demoToken = apiResponse && apiResponse.Token ? apiResponse.Token : '';
    const demoPattern = (apiResponse && apiResponse.Pattern) || 'standard';
    return { demoToken, demoPattern };
}

function applyWebimToken (tokenEntries, pattern) {
    const useLkeV2 = pattern === 'ClawAgent' || (pattern && pattern !== 'standard');
    Object.defineProperty(window, 'webimToken', {
        writable: true,
        enumerable: false,
        configurable: true,
        value: tokenEntries
    });
    Object.defineProperty(window, '$lkeApiVersion', {
        writable: true,
        enumerable: false,
        configurable: true,
        value: useLkeV2 ? 'v2' : 'v1'
    });
    if (useLkeV2) {
        console.log('[LKE] Pattern=%s，使用 V2 SSE 接口', pattern);
    }
    return useLkeV2;
}

async function mountChatApp (useWsMixin) {
    const mixinModule = useWsMixin
        ? await import('@/manage/index')
        : await import('@/manage/sse');
    Vue.mixin(mixinModule.default);
    /* eslint-disable no-new */
    new Vue({
        el: '#app',
        router,
        render (h) {
            return h(App);
        }
    }).$mount('#app');
}

// 移除重复的路由守卫，已在router/index.js中定义

// 检查当前路径是否为 admin 相关页面 - 在初始化之前就判断
// 注意：这是单页应用，使用 Hash 路由，需要检查 hash 部分
const currentPath = window.location.pathname;
const currentHash = window.location.hash;
const isAdminPage = currentPath.includes('/admin') ||
                   currentPath.includes('/admin-login') ||
                   currentHash.includes('/admin') ||
                   currentHash.includes('/admin-login');

if (isAdminPage) {
    // Admin 页面分支 - 直接初始化，不加载聊天相关功能

    // 为 admin 页面添加特殊的全局配置
    Vue.prototype.$isAdminPage = true;

    // 添加 admin 专用的全局方法
    Vue.prototype.$adminUtils = {
        // 检查管理员认证状态
        checkAdminAuth () {
            const token = localStorage.getItem('admin_auth_token');
            const adminInfo = localStorage.getItem('admin_user_info');
            return !!(token && adminInfo);
        },

        // 获取管理员信息
        getAdminInfo () {
            const adminInfo = localStorage.getItem('admin_user_info');
            return adminInfo ? JSON.parse(adminInfo) : null;
        },

        // 清除管理员认证
        clearAdminAuth () {
            localStorage.removeItem('admin_auth_token');
            localStorage.removeItem('admin_user_info');
        },

        // 管理员专用的 API 调用方法
        async adminApiCall (url, options = {}) {
            const token = localStorage.getItem('admin_auth_token');
            const defaultOptions = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            };

            const finalOptions = { ...defaultOptions, ...options };
            if (finalOptions.headers) {
                finalOptions.headers = { ...defaultOptions.headers, ...finalOptions.headers };
            }

            try {
                const response = await fetch(url, finalOptions);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                return await response.json();
            } catch (error) {
                console.error('Admin API 调用失败:', error);
                throw error;
            }
        }
    };

    // 等待 DOM 加载完成后再初始化 Vue 应用
    document.addEventListener('DOMContentLoaded', () => {
        /* eslint-disable no-new */
        new Vue({
            el: '#app',
            router,
            render (h) {
                return h(App);
            }
        }).$mount('#app');
    });

    // 提前结束，不执行后续的聊天相关初始化
    // 注意：这里不能使用 return，因为不在函数内部
    // 后续的聊天相关代码不会执行，因为它们在 if 语句的 else 分支中
} else {
    // 异步初始化聊天相关功能（Token 失败时仍挂载页面，避免白屏）
    (async () => {
        try {
            const lkeComponentModule = await import('lke-component');
            const authManagerModule = await import('@/utils/auth');
            const authManager = authManagerModule.default;
            const lkeComponent = lkeComponentModule.default;

            authManager.ensureGuestUser();
            const guestId = authManager.getCurrentUser().userId;
            Vue.prototype.$authManager = authManager;
            Vue.config.productionTip = false;
            Vue.prototype.moment = moment;

            whenDomReady(() => setupChatVuePlugins(lkeComponent));

            window.$initTypeKey = 5;
            Vue.prototype.webIMSource = 'client';
            Object.defineProperty(window, 'userType', {
                writable: false,
                enumerable: false,
                configurable: false,
                value: 2
            });
            window.$initType = 'ChatDemo';

            let demoToken = '';
            let demoPattern = 'standard';
            try {
                const tokenResult = await fetchLkeToken(guestId);
                demoToken = tokenResult.demoToken;
                demoPattern = tokenResult.demoPattern;
                if (ACCESS_TYPE === 'ws' && !demoToken) {
                    console.warn('getDemoToken 未返回有效 Token，页面仍将加载并稍后重试连接');
                }
            } catch (tokenError) {
                console.error('获取 LKE Token 失败，页面仍将加载:', tokenError);
            }

            const tokenEntries = [{
                type: 5,
                token: demoToken,
                access: ACCESS_TYPE,
                pattern: demoPattern
            }];
            const useLkeV2 = applyWebimToken(tokenEntries, demoPattern);
            const useWsMixin = ACCESS_TYPE === 'ws' && !useLkeV2;

            whenDomReady(() => {
                mountChatApp(useWsMixin).catch((mountError) => {
                    console.error('聊天应用挂载失败:', mountError);
                });
            });
        } catch (error) {
            console.error('聊天应用初始化失败:', error);
            whenDomReady(() => {
                new Vue({
                    el: '#app',
                    router,
                    render (h) {
                        return h(App);
                    }
                }).$mount('#app');
            });
        }
    })();
}
